import { ChevronDownIcon } from "@chakra-ui/icons";
import { Box, Collapse, Text } from "@chakra-ui/react";
import { useState } from "react";
import { FormattedMessage } from "react-intl";

import { AnimateSize } from "@nens/chakra/components";
import { RanaAlert, RanaButton } from "@nens/ranalib";

import { FileDataTypeEnum } from "@/lib/rana-file-data-type";

import { PublicationNoItemText } from "../../components/no-item-text";
import { getFileByPath, getOpenMap } from "../../store/selectors";
import { usePublicationStore } from "../../store/store";
import { Layer } from "../../types/publication";
import { isTypedLayer } from "../../utils/is-layer-typed";
import { PUBLICATION_MAP_ID } from "../map/utils/map-id";

import { LegendLabel } from "./components/legend-label";
import { RasterLegend } from "./components/raster-legend";
import { ScenarioLegend } from "./components/scenario-legend";
import { VectorLegend } from "./components/vector-legend";
import { LEGEND_ITEM_GAP } from "./consts/legend-item-gap";

const PublicationLegendItem = ({ layer }: { layer: Layer }) => {
  const file = usePublicationStore(getFileByPath(layer.filePath));

  if (!file) return null;

  if (!layer.visible) {
    return (
      <Box mb={LEGEND_ITEM_GAP}>
        <LegendLabel layer={layer} file={file} />
        <Text fontStyle="italic" color="brand.grey.500">
          <FormattedMessage
            id="publication.legends.layer_hidden"
            defaultMessage="Layer is hidden"
          />
        </Text>
      </Box>
    );
  }

  if (isTypedLayer(layer, "raster")) {
    return <RasterLegend layer={layer} file={file} />;
  }
  if (file.file.dataType === FileDataTypeEnum.Scenario) {
    return <ScenarioLegend layer={layer} file={file} />;
  }
  if (isTypedLayer(layer, "vector")) {
    return <VectorLegend layer={layer} file={file} mapId={PUBLICATION_MAP_ID} />;
  }

  return null;
};

function SelectionHint() {
  return (
    <RanaAlert onCloseKey="selection_hint" iconSize={6} status="info" fontSize="sm">
      <Box color="brand.analytic" gap={2} mb={1}>
        <Text fontWeight="semibold">
          <FormattedMessage
            id="publication.legends.selection_hint.title"
            defaultMessage="Showing all layer legends"
          />
        </Text>

        <Text>
          <FormattedMessage
            id="publication.legends.selection_hint.body"
            defaultMessage="Click a layer in the left panel to filter the legend to that layer. Hold {ctrl} to select multiple, or {shift} to select a range."
            values={{
              ctrl: <Text as="kbd">Ctrl</Text>,
              shift: <Text as="kbd">Shift</Text>,
            }}
          />
        </Text>
      </Box>
    </RanaAlert>
  );
}

const ShowAllButton = ({ showAll, onClick }: { showAll: boolean; onClick: () => void }) => (
  <RanaButton
    my={2}
    fontWeight={300}
    w="full"
    variant="ghost"
    rightIcon={
      <ChevronDownIcon
        transform={`rotate(${showAll ? 180 : 0}deg)`}
        transition="transform 0.2s ease"
      />
    }
    onClick={onClick}
  >
    {showAll ? (
      <FormattedMessage
        id="publication.legends.hide_all_legends"
        defaultMessage="Hide all legends"
      />
    ) : (
      <FormattedMessage
        id="publication.legends.show_all_legends"
        defaultMessage="Show all legends"
      />
    )}
  </RanaButton>
);

export function PublicationLegends() {
  const { layers } = usePublicationStore(getOpenMap);
  const [showAll, setShowAll] = useState(true);

  const getAllLayers = (layersAndFolders: typeof layers): Layer[] =>
    layersAndFolders.flatMap((layer) =>
      layer.type === "folder" ? getAllLayers(layer.layers) : [layer]
    );
  const allLayers = getAllLayers(layers);

  const nonSelected = !!allLayers.length && !allLayers.some((layer) => layer.selected);

  return (
    <AnimateSize>
      <PublicationNoItemText show={!allLayers.length}>
        <FormattedMessage
          id="publication.legends.no_layers"
          defaultMessage="No legend compatible layers added {lineBreak} to this map yet"
          values={{ lineBreak: <br /> }}
        />
      </PublicationNoItemText>

      <Collapse in={nonSelected} unmountOnExit>
        <SelectionHint />
        <ShowAllButton showAll={showAll} onClick={() => setShowAll(!showAll)} />
      </Collapse>

      {allLayers.map((layer) => {
        return (
          <Collapse key={layer.id} in={(nonSelected && showAll) || layer.selected} unmountOnExit>
            <PublicationLegendItem layer={layer} key={layer.id} />
          </Collapse>
        );
      })}
    </AnimateSize>
  );
}
